import { CareHomeShortlistData } from '@/careHomeShortlistData';
import { CareCircle, CareCircleMember, Role } from '../careCircle';
import { CareProfileFormEvent, CareProfileLabeledAnswer } from '@/careProfile';
import type { PaginationMeta } from '@/pagination';

export interface OrganizationType {
  id: string;
  name: string;
}

export interface OrganizationDomain {
  id: string;
  domain: string;
  organizationID: string;
}

export type OrganizationTypeID =
  'sjp-ifa' | 'ifa' | 'unverified' | 'enterprise' | 'none';

export const OrganizationTypeID = {
  SJP_IFA: 'sjp-ifa',
  IFA: 'ifa',
  UNVERIFIED: 'unverified',
  ENTERPRISE: 'enterprise',
  NONE: 'none',
} as const satisfies Record<string, OrganizationTypeID>;

export function isIFAOrganizationTypeID(
  organizationTypeId?: OrganizationTypeID,
): boolean {
  if (!organizationTypeId) return false;
  return organizationTypeId === 'sjp-ifa' || organizationTypeId === 'ifa';
}

export interface Organization {
  id: string;
  name: string;
  inviteKeyName?: string;
  isInviteKeyEnabled?: boolean;
  isInviteCode?: boolean;
  isEmailRegistrationEnabled?: boolean;
  organizationTypeId?: OrganizationTypeID;
  organizationType?: OrganizationType;
  organizationDomains?: OrganizationDomain[];
  isPrivateEmail?: boolean;
  typeformID?: string;
  /** Referring partner ID when set via admin. Used to link/unlink org to partner. */
  partnerId?: string | null;
  /** Display name of the referring partner when partnerId is set (read-only from API). */
  partnerName?: string | null;
  isCareAssessmentEnabled?: boolean;
  isBenefitsCalculatorEnabled?: boolean;
  isPoaEnabled?: boolean;
}

export interface OrganizationInfo {
  id: string;
  name: string;
  isInviteKeyEnabled?: boolean;
  isInviteCode?: boolean;
  isEmailRegistrationEnabled?: boolean;
  inviteKeyName?: string;
}

export interface VerificationConfiguration {
  types: string[];
  inviteKeyName?: string;
}

export interface AccountRole {
  id: string;
  name: string;
}

export interface KindeUser {
  id: string;
  email: string;
  kindeID: string;
  picture: string;
  fullName: string;
  firstName: string;
  lastName: string;
  isSuspended?: boolean;
  createdOn?: string;
}

export interface AccountConsent {
  accountID?: string;
  consentFormId: string;
  acceptedAt?: Date;
}

export interface Account {
  id: string;
  imgSrc: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  kindeEmail: string;
  kindeAuthType?: string;
  kindeUserID?: string;
  kindeUser?: KindeUser;
  organizationID?: Organization;
  organization: Organization;
  careCircleMembers: CareCircleMember[];
  accountRoleID?: string;
  accountRole: AccountRole;
  isOnboarded?: boolean;
  isOnboardedDashboard?: boolean;
  isOnboardedArticles?: boolean;
  isMigrationEmailReceived?: boolean;
  referLtcCode?: string;
  partnerCode?: string;
  partnerId?: string;
  careProfileFormEvents: CareProfileFormEvent[];
  accountConsents?: AccountConsent[];
  isConsent?: boolean;
  isNewKindeAccount?: boolean;
  isCreateReferralOrganisation?: boolean;
  avatarConfig?: string;
  avatarUrl?: string;
  isPoaFreeReview?: boolean;
  features?: AccountFeature[];
}

export enum AccountFeatureName {
  CARE_ASSESSMENT = 'care-assessment',
  POWER_OF_ATTORNEY = 'power-of-attorney',
}

export interface AccountFeature {
  name: AccountFeatureName;
  value: boolean;
}

export interface AccountSummary {
  id: string;
  imgSrc: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email?: string;
  inviteKey?: string;
  kindeEmail: string;
  organization: Organization;
  careCircleMembers: CareCircleMember[];
  accountRole: AccountRole;
  isRegistered?: boolean;
  isOnboarded?: boolean;
  isOnboardedDashboard?: boolean;
  isOnboardedArticles?: boolean;
  careProfileFormEvents: CareProfileFormEvent[];
  isConsent?: boolean;
  avatarUrl?: string;
}

export interface CareRecipientSummary {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  careCircleID: string;
  careRecipientAssessmentStatus: string;
  careRecipientAssessmentDate: string;
  careRecipientShortlistData: CareHomeShortlistData[];
  securedRegulatoryID?: string;
  careRecipientFinancialAssessmentStatus: string;
  careRecipientFinancialAssessmentDate: string;
  careRecipientFinancialAssessmentAnswers: CareProfileLabeledAnswer[];
}

export interface CredentialsKinde {
  code: string;
}

export interface AddInviteKeys {
  inviteKeys: string[];
}

export interface AdminAddInviteKeys extends AddInviteKeys {
  organizationID: string;
}

export interface InviteMemberRequest {
  emails: string[];
  isForceSend?: boolean;
}

export interface AdminInviteMemberRequest extends InviteMemberRequest {
  organizationID: string;
}

export interface InviteOrganizationAdminRequest {
  email: string;
  firstName: string;
  lastName: string;
  organizationID: string;
}

export interface LdUser {
  key: string;
  name: string;
  email: string;
  kindeEmail: string;
}

export interface LdOrganization {
  key: string;
  name: string;
}

export interface LdContext {
  kind: string;
  user: LdUser;
  organization: LdOrganization;
}

export interface MemberRegisterCredentials {
  email: string;
}

export interface CreateMember {
  caregiverFirstName: string;
  careRecipientFirstName: string;
}

export interface InvitedMember {
  id: string;
  organizationId: string;
  email: string;
  inviteKey: string;
  inviterAccountId?: string;
  inviteSentAt?: string;
  careCircleId?: string;
  careCircle?: CareCircle;
  roleId?: string;
  role?: Role;
  firstName?: string;
  careRecipientAccountRelationshipId?: string;
  isAwaitingFee?: boolean;
}

export enum KindeIdentityType {
  Email = 'email',
  Google = 'oauth2:google',
  Facebook = 'oauth2:facebook',
  Apple = 'oauth2:apple',
}

export const prettyKindeIdentityType = (type?: KindeIdentityType | string) => {
  switch (type) {
    case KindeIdentityType.Email:
      return 'Email';
    case KindeIdentityType.Google:
      return 'Google';
    case KindeIdentityType.Facebook:
      return 'Facebook';
    case KindeIdentityType.Apple:
      return 'Apple';
    default:
      return 'None';
  }
};

export interface PushNotificationToken {
  token: string;
  accountID?: string;
  platform: string;
  environment?: string;
}

export interface ReferringPartner {
  id: string;
  name: string;
  domains: string[];
}

/** Org ref with id and name so the UI can render without a second API call. */
export interface ReferringPartnerOrganizationRef {
  id: string;
  name: string;
}

/** Partner plus linked org refs (id + name). organizationIds derived for compatibility. */
export interface ReferringPartnerWithOrganizations {
  partner: ReferringPartner;
  /** Organizations linked to this partner (id + name from list response). */
  organizations: ReferringPartnerOrganizationRef[];
  /** Derived from organizations for backward compatibility. */
  organizationIds: string[];
}

export interface CreateReferringPartnerRequest {
  name: string;
  domains: string[];
}

export interface UpdateReferringPartnerRequest {
  name: string;
  domains: string[];
}

export interface PaginatedAccounts {
  data: Account[];
  pagination: PaginationMeta;
}

export interface PaginatedOrganizations {
  data: Organization[];
  pagination: PaginationMeta;
}
